import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Users, Car, MapTrifold, Trophy, Heart, Eye, ShieldCheck } from '@phosphor-icons/react';
import { useState, useEffect, useRef } from 'react';

const SERVICE_IMG = "./img/presentationphoto.webp";

function AnimatedCounter({ end, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        let start = 0;
        const duration = 2000;
        const increment = end / (duration / 16);
        const timer = setInterval(() => {
          start += increment;
          if (start >= end) { setCount(end); clearInterval(timer); }
          else setCount(Math.floor(start));
        }, 16);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);
  return <span ref={ref}>{count}{suffix}</span>;
}

export default function Presentation() {
  const navigate = useNavigate();
  const stats = [
    { icon: Car, value: 3, suffix: '', label: 'Types de vehicules' },
    { icon: Users, value: 1, suffix: '+', label: 'Chauffeur professionnel' },
    { icon: MapTrifold, value: 500, suffix: '+', label: 'Trajets effectues' },
    { icon: Trophy, value: 98, suffix: '%', label: 'Satisfaction client' },
  ];
  const values = [
    { icon: Heart, title: 'Professionnalisme', desc: 'Chaque detail compte. Nos chauffeurs sont formes aux standards les plus eleves du service VTC.' },
    { icon: Eye, title: 'Transparence', desc: 'Tarifs clairs, sans surprise. Vous connaissez le prix exact avant de reserver.' },
    { icon: ShieldCheck, title: 'Assurance', desc: 'Vehicules assures tous risques. Votre securite est notre priorite absolue.' },
  ];

  return (
    <div className="pt-20" data-testid="presentation-page">
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={SERVICE_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-desti-black via-desti-black/80 to-desti-black" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">Notre histoire</p>
            <h1 className="font-heading text-4xl sm:text-5xl text-white max-w-2xl leading-[1.15]">L'excellence du transport prive</h1>
            <p className="mt-6 text-lg text-white/60 max-w-2xl font-body font-light leading-relaxed">
              Fondée en 2025 par JOVENEL Destina, DestiDriving est née d'une vision simple : offrir un service de transport
              privé qui allie le confort d'une berline de luxe a la fiabilite d'un service professionnel. Basée à Ablon sur seine,
              notre entreprise déssert l'ensemble de l'Ile-de-France et les principaux aéroports.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-desti-surface/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="text-center p-6" data-testid={`stat-${i}`}>
                <s.icon size={32} weight="duotone" className="text-desti-gold mx-auto mb-4" />
                <p className="font-heading text-4xl text-white"><AnimatedCounter end={s.value} suffix={s.suffix} /></p>
                <p className="text-sm text-desti-text mt-2">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">Ce qui nous definit</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-white">Nos valeurs</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-desti-surface border border-white/5 p-8 hover:-translate-y-1 transition-transform duration-300" data-testid={`value-${i}`}>
                <v.icon size={36} weight="duotone" className="text-desti-gold mb-5" />
                <h3 className="font-heading text-xl text-white mb-3">{v.title}</h3>
                <p className="text-sm text-desti-text leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-desti-surface/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-6">Temoignage</p>
          <blockquote className="font-heading text-2xl sm:text-3xl text-white italic leading-relaxed">
            "DestiDriving a transforme mes deplacements professionnels. Le service est impeccable, les chauffeurs sont d'une ponctualite exemplaire."
          </blockquote>
          <p className="mt-6 text-sm text-desti-text">- Client fidele depuis 2025</p>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl text-white mb-6">Pret a voyager avec nous ?</h2>
          <button onClick={() => navigate('/')} className="bg-desti-gold text-desti-black font-medium text-sm tracking-wide px-8 py-4 hover:bg-desti-gold-hover transition-colors" data-testid="presentation-cta">
            Reserver maintenant
          </button>
        </div>
      </section>
    </div>
  );
}
